import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { catchError, distinctUntilChanged, mergeMap, shareReplay, switchMap, tap } from 'rxjs/operators';
import { orderBy, isNil, isEmpty } from 'lodash';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { UserRegion, UserRegionService } from './api/user-regions.service';
import { CookieService } from 'ngx-cookie-service';
import { UserService } from './api/user.service';
import { UserMunicipalities } from '../models/user';

@Injectable({
  providedIn: 'root',
})
export class UserMunicipalitiesService {
  private _municipalityData: MapStoreMunicipality[] = [];
  private _currentMunicipalityData: MapStoreMunicipality = {} as MapStoreMunicipality;
  private readonly _municipalities$ = new BehaviorSubject<MapStoreMunicipality[]>([]);
  private readonly _regions$ = new BehaviorSubject<UserRegion[]>([]);
  private readonly _currentMunicipality$ = new BehaviorSubject<MapStoreMunicipality>({} as MapStoreMunicipality);

  public get municipalities$(): Observable<MapStoreMunicipality[]> {
    return this._municipalities$.asObservable();
  }

  public get municipalities(): MapStoreMunicipality[] {
    return this._municipalities$.value;
  }

  public get regions$(): Observable<UserRegion[]> {
    return this._regions$.asObservable();
  }

  public get regions(): UserRegion[] {
    return this._regions$.value;
  }

  public get currentMunicipality$(): Observable<MapStoreMunicipality> {
    return this._currentMunicipality$.pipe(distinctUntilChanged(), shareReplay());
  }

  public get currentMunicipality(): MapStoreMunicipality {
    return this._currentMunicipality$.value;
  }

  constructor(
    private readonly authService: AuthService,
    private readonly userRegionsService: UserRegionService,
    private readonly userService: UserService,
    private readonly cookieService: CookieService,
  ) {}

  public updateMunicipality(value: MapStoreMunicipality) {
    this._currentMunicipalityData = value;
    this._currentMunicipality$.next(this._currentMunicipalityData);
  }

  public initMunicipalities(): Observable<any> {
    return this.userService.getUserMunicipalities(this.authService.currentUser.guid).pipe(
      switchMap((data: UserMunicipalities[]) => {
        if (isNil(data) || isEmpty(data)) {
          if (this.authService.isDsoUser) {
            return of([]);
          } else {
            return throwError(() => new Error('No municipalities found for user'));
          }
        } else {
          return of(data);
        }
      }),
      tap((data: UserMunicipalities[]) => {
        if (!isNil(data) && !isEmpty(data)) {
          const userMunicipalities: MapStoreMunicipality[] = data.map((a: UserMunicipalities) => {
            return {
              key: a.municipalityKey,
              name: a.municipalityName,
              guid: a.municipalityId,
              postCode: a.zipCode,
              isActive: a.isActive,
              isDeactivated: a.isDeactivated,
            };
          });
          this.updateMunicipalities(userMunicipalities);
        }
      }),
      mergeMap((data: UserMunicipalities[]) => {
        if (!isNil(data) && !isEmpty(data)) {
          this.authService.applyMunicipalityRestrictions(this.currentMunicipality.guid);
        }
        return of();
      }),
    );
  }

  public initRegions() {
    return this.userService.getDelegates(this.authService.currentUser.guid).pipe(
      catchError(this.errorHandler),
      mergeMap((d: any) => {
        const delegates = d.map((a: any) => a.guid);
        delegates.push(this.authService.currentUser.guid);
        return this.userRegionsService.getByUserRegionViewUserIds(delegates).pipe(catchError(this.errorHandler));
      }),
      tap((data) => this._regions$.next(data)),
    );
  }

  private errorHandler(e: any): Observable<any> {
    if (e.status === 404) {
      return of([]);
    }
    return throwError(() => e);
  }

  public updateName(municipality: any): void {
    const data = this._municipalityData.find((m) => m.key === municipality.municipalityKey);
    if (data) {
      data.name = municipality.name;
      data.isDeactivated = municipality.isDeactivated;
      this._municipalities$.next(this._municipalityData);
    }
  }

  private checkAccess(municipalities: MapStoreMunicipality[]): void {
    const allDeactivated = municipalities.every((municipality: MapStoreMunicipality) => municipality.isDeactivated);
    if (allDeactivated || municipalities.length <= 0) {
      localStorage.clear();
      this.cookieService.deleteAll('/');
      sessionStorage.clear();
      let errorCode = '401.3';
      if (municipalities.length === 1) {
        errorCode = `401.2?municipality=${municipalities[0].name}`;
      }
      globalThis.location.href = `${globalThis.location.protocol}//${globalThis.location.host}/error/${errorCode}`;
    }
  }

  private updateMunicipalities(userMunicipalities: MapStoreMunicipality[]): void {
    this.checkAccess(userMunicipalities);
    if (!this.authService.isSystemAdmin) {
      userMunicipalities = userMunicipalities.filter((a) => !a.isDeactivated);
    }
    this._municipalityData = orderBy(userMunicipalities, ['name'], ['asc']);
    const selectedMunicipality = this._currentMunicipalityData.key
      ? (this._municipalityData.find((m) => m.key === this._currentMunicipalityData.key) as MapStoreMunicipality)
      : this._municipalityData[0];

    this.updateMunicipality(selectedMunicipality);
    this._municipalities$.next(this._municipalityData);
  }
}
